import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { TSTreeNode } from 'tsui';

import { SettingsService } from '@modules/settings/settings.service';

import { CelesteHostToolsConstants } from './celeste-host-tools.constants';
import { HostTool, HostToolsProvider } from './host-tool.model';

interface NavigateToPageArgs {
  destination: string;
}

interface SettingsDestination {
  key: string;
  path: string;
  label: string;
}

const BASE_DESTINATION_ROUTES: Record<string, string> = {
  settings: '/settings',
  deals: '/projects',
  maps: '/research',
  pdf_report: '/reporting/list?type=pdf',
  report_builder: '/reporting/list?type=builder',
};

/** Builds the `navigate_to_page` Celeste host tool, letting the chat agent drive in-app navigation. */
@Injectable({
  providedIn: 'root',
})
export class NavigationHostToolService implements HostToolsProvider {
  private readonly router = inject(Router);
  private readonly settingsService = inject(SettingsService);

  public getTool(): HostTool<NavigateToPageArgs, { navigated: boolean; destination: string }> {
    const settingsDestinations = this.getSettingsDestinations();
    const destinationRoutes = this.buildDestinationRoutes(settingsDestinations);

    return {
      name: 'navigate_to_page',
      description: this.buildDescription(settingsDestinations),
      inputSchema: {
        type: 'object',
        properties: {
          destination: {
            type: 'string',
            enum: Object.keys(destinationRoutes),
            description: 'The section of the app to navigate to.',
          },
        },
        required: ['destination'],
      },
      handler: (args) => this.navigate(args, destinationRoutes),
    };
  }

  /** Flattens the permission/feature-flag-filtered settings tree into navigable leaf pages. */
  private getSettingsDestinations(): SettingsDestination[] {
    const flatten = (nodes: TSTreeNode[]): SettingsDestination[] =>
      nodes.reduce((destinations: SettingsDestination[], node) => {
        if (node.children?.length) {
          return [...destinations, ...flatten(node.children)];
        }

        if (node.link) {
          return [...destinations, { key: node.link, path: `/${node.link}`, label: node.name }];
        }

        return destinations;
      }, []);

    return flatten(this.settingsService.settingsOptions);
  }

  private buildDestinationRoutes(settingsDestinations: SettingsDestination[]): Record<string, string> {
    const settingsRoutes = settingsDestinations.reduce(
      (routes: Record<string, string>, { key, path }) => ({ ...routes, [key]: path }),
      {},
    );

    return { ...BASE_DESTINATION_ROUTES, ...settingsRoutes };
  }

  private buildDescription(settingsDestinations: SettingsDestination[]): string {
    const settingsList = settingsDestinations.map(({ key, label }) => `"${key}" (${label})`).join(', ');

    return (
      CelesteHostToolsConstants.REPORT_TYPE_AMBIGUITY_DISCLAIMER +
      'Navigates the host application to a section. Base sections: "settings" (settings home), ' +
      '"deals" (deals/projects list), "maps" (research/map view), ' +
      `Settings sub-pages: ${settingsList}. `
    );
  }

  private async navigate(
    args: NavigateToPageArgs,
    destinationRoutes: Record<string, string>,
  ): Promise<{ navigated: boolean; destination: string }> {
    const path = destinationRoutes[args.destination];

    if (!path) {
      throw new Error(`Unknown destination "${args.destination}".`);
    }

    const navigated = await this.router.navigateByUrl(path);

    if (!navigated) {
      throw new Error(`Navigation to "${args.destination}" was blocked.`);
    }

    return { navigated: true, destination: args.destination };
  }
}
